// AI/SEO enriched titles, descriptions, offers per service slug & locale.
// Used to enrich <title>, <meta description> and Service JSON-LD without
// changing the visual H1 of each page.

type Locale = "tr" | "en" | "de" | "es" | "ru" | "fr" | "it";

type ServiceSeo = {
  seoTitle: Partial<Record<Locale, string>>;
  seoDescription: Partial<Record<Locale, string>>;
  offers: string[]; // catalog item names (locale-neutral keywords)
  keywords?: string[];
  areaServed?: any; // schema.org areaServed override (geo signal for local SEO/GEO)
};

// İstanbul Anadolu Yakası ilçeleri — koruma-ve-guvenlik için yerel arama sinyali.
const ANADOLU_YAKASI_AREA = [
  { "@type": "City", name: "İstanbul" },
  ...[
    "Kadıköy", "Ataşehir", "Ümraniye", "Üsküdar", "Maltepe", "Kartal",
    "Pendik", "Tuzla", "Sancaktepe", "Çekmeköy", "Beykoz", "Sultanbeyli", "Şile",
  ].map((name) => ({ "@type": "AdministrativeArea", name })),
];

const data: Record<string, ServiceSeo> = {
  "koruma-ve-guvenlik": {
    seoTitle: {
      tr: "Anadolu Yakası Özel Güvenlik Şirketi",
      en: "Private Security Services | 24/7 Protection under Law No. 5188",
      de: "Privater Sicherheitsdienst | 24/7-Schutz nach Gesetz Nr. 5188",
      es: "Servicios de Seguridad Privada | Protección 24/7 conforme a la Ley 5188",
      ru: "Услуги частной охраны | Защита 24/7 по закону № 5188",
      fr: "Services de Sécurité Privée | Protection 24/7 selon la Loi 5188",
      it: "Servizi di Sicurezza Privata | Protezione 24/7 ai sensi della Legge 5188",
    },
    seoDescription: {
      tr: "5188 belgeli silahlı/silahsız personel, devriye, VIP koruma ve etkinlik güvenliği. Anadolu Yakası, 20+ yıl deneyim, 7/24 operasyon. Keşif: 0216 222 11 44.",
      en: "Private security under Turkey's Law 5188: armed/unarmed officers, patrol, VIP & event security, risk analysis. 20+ years of field experience, 24/7 operations.",
      it: "Sicurezza privata ai sensi della Legge turca 5188: personale armato/non armato, pattugliamento, sicurezza VIP ed eventi, analisi dei rischi. 20+ anni di esperienza sul campo, operazioni 24/7.",
    },
    offers: [
      "Sabit Nokta Güvenliği",
      "Mobil ve Yaya Devriye",
      "VIP ve Yakın Koruma",
      "Etkinlik ve Organizasyon Güvenliği",
      "Risk Analizi ve Güvenlik Danışmanlığı",
      "Acil Durum ve Kriz Yönetimi",
    ],
    keywords: ["özel güvenlik", "güvenlik şirketi", "anadolu yakası güvenlik", "anadolu yakası güvenlik firması", "anadolu yakası özel güvenlik şirketi", "5188", "VIP koruma", "etkinlik güvenliği"],
    areaServed: ANADOLU_YAKASI_AREA,
  },
  "zirhli-arac-ve-tasima": {
    seoTitle: {
      tr: "Zırhlı Araç Tahsisi ve Kıymet Nakli",
      en: "Armored Vehicle Allocation & Secure Transport | Valuables, VIP",
      it: "Veicoli Blindati e Trasporto Sicuro | Trasporto Valori, VIP",
    },
    seoDescription: {
      tr: "Zırhlı araç tahsisi, para ve kıymetli eşya nakli, VIP şoförlü araç, koruma refakatinde konvoy. Anadolu Yakası merkezli, 5188 kapsamında eğitimli ekipler.",
      en: "Armored vehicle allocation, cash and valuables transport, chauffeured VIP vehicles, escorted convoys and critical cargo movement — Istanbul-based teams trained under Law No. 5188.",
      it: "Assegnazione di veicoli blindati, trasporto di contante e valori, auto VIP con autista, convogli scortati e carichi critici — squadre di Istanbul formate ai sensi della Legge 5188.",
    },
    offers: [
      "Para ve Kıymetli Eşya Nakli",
      "Kasa, Şube ve Merkez Transferleri",
      "VIP ve Yönetici İçin Şoförlü Zırhlı Araç Tahsisi",
      "Koruma Refakatinde Konvoy ve Güzergâh Yönetimi",
      "Kurumsal Personel Taşıma",
      "Kritik Ekipman ve Hassas Yük Nakli",
      "Etkinlik ve Proje Bazlı Kısa Süreli Araç Tahsisi",
    ],
    keywords: ["zırhlı araç tahsisi", "zırhlı araç kiralama", "kıymet nakli", "para nakli", "değerli eşya taşıma", "VIP araç tahsisi", "anadolu yakası zırhlı araç", "güvenlikli taşıma"],
    areaServed: ANADOLU_YAKASI_AREA,
  },
  "tesis-yonetimi": {
    seoTitle: {
      tr: "Tesis Yönetimi: Site, Plaza ve AVM",
      en: "Integrated Facility Management (IFM) | Residences, Plazas, Malls",
      it: "Integrated Facility Management (IFM) | Residenze, Plazas, Centri Commerciali",
    },
    seoDescription: {
      tr: "Apartman, site, plaza ve AVM için teknik bakım, aidat tahsilatı, tedarikçi ve enerji yönetimi. 2004'ten beri 500+ müşteri, tek muhatap çözüm.",
      en: "Integrated Facility Management for residences, plazas and malls: technical maintenance, billing, vendor management, energy tracking, regulatory compliance — single point of contact.",
      it: "Integrated Facility Management per residenze, plazas e centri commerciali: manutenzione tecnica, fatturazione, gestione fornitori, monitoraggio energetico, conformità normativa — punto di contatto unico.",
    },
    offers: [
      "Teknik Bakım ve Onarım",
      "Bütçe ve Finansal Raporlama",
      "Aidat ve Tahsilat Yönetimi",
      "Tedarikçi ve Sözleşme Yönetimi",
      "Enerji ve Su Tüketimi İzleme",
      "KMK / İş Güvenliği / KVKK Uyumu",
      "Sakin İletişimi ve Şikâyet Yönetimi",
    ],
    keywords: ["tesis yönetimi", "site yönetimi", "apartman yönetimi", "IFM", "AVM yönetimi", "profesyonel tesis yönetimi"],
  },
  "ilaclama": {
    seoTitle: {
      tr: "İlaçlama ve Haşere Kontrolü | Ruhsatlı",
      en: "Pest Control & Disinfection | Ministry of Health Licensed, HACCP-Compliant",
      it: "Disinfestazione e Disinfezione | Autorizzata dal Ministero della Salute, Conforme HACCP",
    },
    seoDescription: {
      tr: "Sağlık Bakanlığı ruhsatlı ilaçlama: hamamböceği, fare, sinek, yatak böceği, dezenfeksiyon. HACCP/IPM uyumlu, MSDS ve servis raporu garantili.",
      en: "Ministry-of-Health-licensed pest control: cockroaches, rodents, flies, bed-bugs, disinfection, HACCP IPM. Certified applicators, MSDS and service report included.",
      it: "Disinfestazione autorizzata dal Ministero della Salute: scarafaggi, roditori, mosche, cimici dei letti, disinfezione, HACCP IPM. Applicatori certificati, MSDS e report di servizio inclusi.",
    },
    offers: [
      "Hamamböceği, Karınca, Sinek Mücadelesi",
      "Fare ve Kemirgen Kontrolü",
      "Yatak Böceği Uygulaması",
      "Genel Dezenfeksiyon",
      "ULV Dış Mekan Uygulaması",
      "HACCP Uyumlu Gıda İşletmesi IPM",
    ],
    keywords: ["ilaçlama", "haşere kontrolü", "dezenfeksiyon", "HACCP", "IPM"],
  },
  "genel-temizlik": {
    seoTitle: {
      tr: "Genel Temizlik: Ofis, Plaza, Hastane",
      en: "General Cleaning Services | Offices, Plazas, Hospitals",
      it: "Servizi di Pulizia Generale | Uffici, Plazas, Ospedali",
    },
    seoDescription: {
      tr: "Ofis, plaza, hastane ve fabrikalar için hijyen odaklı temizlik: detay temizlik, cam-cephe, post-construction, halı yıkama. Biyosidal sertifikalı ürün.",
      en: "Hygiene-focused professional cleaning for offices, plazas, hospitals and factories: deep clean, façade, post-construction, carpet — biocide-certified products.",
      it: "Pulizia professionale orientata all'igiene per uffici, plazas, ospedali e fabbriche: pulizia profonda, facciate, post-cantiere, tappeti — prodotti certificati biocidi.",
    },
    offers: [
      "Günlük Ofis ve Ortak Alan Temizliği",
      "Detay (Derin) Temizlik",
      "Cam ve Dış Cephe Temizliği",
      "İnşaat Sonrası Temizlik",
      "Halı, Koltuk ve Yüzey Yıkama",
      "Hastane ve Sağlık Tesisi Hijyenik Temizlik",
      "Endüstriyel Üretim Alanı Temizliği",
    ],
    keywords: ["temizlik şirketi", "ofis temizliği", "hastane temizliği", "cephe temizliği", "post construction"],
  },
  "elektronik-guvenlik-sistemleri": {
    seoTitle: {
      tr: "Elektronik Güvenlik: CCTV, Alarm, Geçiş",
      en: "Electronic Security Systems | CCTV, Access Control, Alarm, Fire",
      it: "Sistemi di Sicurezza Elettronica | TVCC, Controllo Accessi, Allarme, Antincendio",
    },
    seoDescription: {
      tr: "CCTV, geçiş kontrol, alarm, yangın algılama ve plaka tanıma (LPR) sistemleri: risk analizi, projelendirme, kurulum ve 7/24 teknik bakım. KVKK uyumlu.",
      en: "CCTV, access control, alarm, fire detection and LPR systems: risk analysis, design, installation and 24/7 maintenance. KVKK/GDPR-compliant.",
      it: "Sistemi TVCC, controllo accessi, allarme, rilevamento incendi e LPR: analisi rischi, progettazione, installazione e manutenzione 24/7. Conforme KVKK/GDPR.",
    },
    offers: [
      "IP / CCTV Kamera Sistemleri",
      "Geçiş Kontrol ve Turnike",
      "Hırsız Alarm Sistemleri",
      "Yangın Algılama ve Söndürme",
      "Plaka Tanıma (LPR) ve Otopark Yönetimi",
      "Diafon ve Görüntülü İnterkom",
      "Periyodik Bakım ve 7/24 Destek",
    ],
    keywords: ["CCTV", "kamera sistemleri", "geçiş kontrol", "alarm sistemi", "yangın algılama", "LPR"],
  },
};

export function getServiceSeo(slug: string, locale: Locale) {
  const entry = data[slug];
  if (!entry) return null;
  const seoTitle = entry.seoTitle[locale] || entry.seoTitle.tr || entry.seoTitle.en;
  const seoDescription =
    entry.seoDescription[locale] || entry.seoDescription.tr || entry.seoDescription.en;
  return {
    seoTitle: seoTitle!,
    seoDescription: seoDescription!,
    offers: entry.offers,
    keywords: entry.keywords ?? [],
    areaServed: entry.areaServed,
  };
}
